import {
  manuallyTriggerMailroomSync,
  markMailAsRead,
  STABLE_SUPPORT_STATE_JURISDICTION,
  useBusinessEntity,
  useBusinessEntityLocations,
  useJobs,
  useMailrooms,
  useMails,
  useRegisteredCompanies,
  useJurisdictions,
} from '@commenda/api';
import {
  Button,
  HorizontalTabsList,
  Icons,
  LoadingIndicator,
  Tabs,
  TabsContent,
  TabsTrigger,
  useToggle,
} from '@commenda/kakashi';
import { LocationTypeRevamp } from '@commenda/types';
import { cn } from '@commenda/utils';
import { JobType, MailroomType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { EmptyStateV2 } from '../../../components/empty-state/EmptyStateV2';
import { useNotification } from '../../../hooks/useNotification';
import { MailCard } from '../MailCard';
import { MailItemPreview } from '../MailItemPreview';
import { AddExistingDRACompanyModal } from '../Modals/AddExistingDRACompany';
import { AddStableAddressModal } from '../Modals/AddStableAddressModal';

enum MailroomTab {
  ALL = 'all',
  UNREAD = 'unread',
}

type MailroomAdminProps = {
  businessEntityId: number;
};

export const MailroomAdmin = ({ businessEntityId }: MailroomAdminProps) => {
  const { setNotification } = useNotification();
  const { labelOf } = useJurisdictions();
  const { data: businessEntity, refetch: refetchBusinessEntity } =
    useBusinessEntity(businessEntityId);
  const { data: registeredCompanies, mutate: mutateRegisteredCompanies } =
    useRegisteredCompanies();
  const { data: mailroomData, refetch: refetchMailroomData } = useMailrooms({
    corporationId: businessEntityId,
  });

  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [mailTab, setMailTab] = useState<MailroomTab>(MailroomTab.ALL);

  const addExistingDRACompanyModalVisibility = useToggle();

  const { data: businessEntityLocationsResponse } =
    useBusinessEntityLocations(businessEntityId);

  const { data: mailroomJobs, mutate: mutateMailroomJobs } = useJobs({
    jobType: JobType.adding_mailing_address_using_stable,
    corporationId: businessEntityId,
  });

  const pendingJobs = mailroomJobs?.jobs?.filter((job) => !job.isJobCompleted);
  const hasPendingJobs = pendingJobs?.length > 0;

  const addStableAddressModalVisibility = useToggle();
  const manualSyncLoading = useToggle();

  const isRegisteredAgentDocumentAccessEnabled =
    businessEntity?.onboardingInfo?.registeredAgentDocumentAccess ?? false;

  const draDocsAccessVisibility = useToggle(
    isRegisteredAgentDocumentAccessEnabled
  );

  const mailrooms = mailroomData?.mailrooms;
  const {
    data: mailsData,
    error: mailsError,
    refetch: refetchMails,
  } = useMails({
    mailroomIds: mailrooms?.map((mailroom) => mailroom.id),
    includeFiles: true,
  });
  const mails = mailsData?.mails ?? [];

  const mailItemsWithMailroom =
    mails?.map((mail) => {
      const mailroom = mailrooms?.find((mr) => mr.id === mail.mailroomId);

      return {
        ...mail,
        mailroomId: mailroom?.id,
        mailroom,
      };
    }) ?? [];

  // Set initial selected mail if none is selected
  useEffect(() => {
    if (!selectedMailId && mailItemsWithMailroom.length > 0) {
      setSelectedMailId(mailItemsWithMailroom[0].id);
    }
  }, [mailItemsWithMailroom, selectedMailId]);

  useEffect(() => {
    if (isRegisteredAgentDocumentAccessEnabled) draDocsAccessVisibility.on();
    else draDocsAccessVisibility.off();
  }, [isRegisteredAgentDocumentAccessEnabled, draDocsAccessVisibility]);

  async function handleMailRead(mailId: string) {
    setSelectedMailId(mailId);
    const res = await markMailAsRead(mailId);

    if (!res.ok) {
      setNotification({
        title: 'Failed to update mail',
        message: 'Please try again',
        valid: false,
        show: true,
      });
    }
  }

  const handleTabChange = (selectedTab: MailroomTab) => {
    setMailTab(selectedTab);
    refetchMails();
  };

  const selectedMail = mailItemsWithMailroom.find(
    (mail) => mail.id === selectedMailId
  );

  const unreadMails = mailItemsWithMailroom.filter((mail) => !mail.isRead);

  if (
    !businessEntity ||
    !mailroomData ||
    !mailroomJobs ||
    !registeredCompanies ||
    !businessEntityLocationsResponse
  ) {
    return <LoadingIndicator />;
  }

  const { jurisdiction } = businessEntity.generalCorporationInfo;

  const isStableSupportedState =
    jurisdiction && STABLE_SUPPORT_STATE_JURISDICTION.includes(jurisdiction);

  const registeredAgentMailroom = mailrooms?.find(
    (mailroom) => mailroom.mailroomType === MailroomType.REGISTERED_AGENT
  );

  const virtualMailroom = mailrooms?.find(
    (mailroom) => mailroom.mailroomType === MailroomType.VIRTUAL_ADDRESS
  );
  const hasVirtualMailroom = !!virtualMailroom;

  const mailingAddressLocation = businessEntityLocationsResponse.locations.find(
    (location) =>
      location.locationTypes?.includes(LocationTypeRevamp.MAILING_ADDRESS)
  );
  const hasMailingAddress = !!mailingAddressLocation;

  const connectedRegisteredAgentCompany = registeredCompanies?.find(
    (company) => company.id === registeredAgentMailroom?.sourceId
  );
  const hasConnectedRegisteredAgentCompany = !!connectedRegisteredAgentCompany;

  return (
    <div className="flex flex-col gap-8">
      {/* Mailroom Configuration Section */}
      <div>
        <h1 className="txt-large-bold">Registered Agents Inc.</h1>
        <p className="txt-sm text-text-secondary font-medium">
          This is the registered agent for your company. It is used to send and
          receive mail to and from your company.
        </p>
        <div className="flex items-center justify-between gap-2 mt-4">
          <div className="flex items-center gap-2">
            <Button
              disabled={hasConnectedRegisteredAgentCompany}
              size="sm"
              onClick={addExistingDRACompanyModalVisibility.show}
            >
              Add existing Registered Agents Inc Company
            </Button>
            {!!registeredAgentMailroom && (
              <Button
                size="sm"
                disabled={manualSyncLoading.visible}
                onClick={async () => {
                  if (!registeredAgentMailroom?.id) return;
                  manualSyncLoading.on();
                  const res = await manuallyTriggerMailroomSync(
                    registeredAgentMailroom.id
                  );
                  manualSyncLoading.off();
                  if (!res.success) {
                    setNotification({
                      message: res.message,
                      show: true,
                      title: 'Failed',
                      valid: false,
                    });
                    return;
                  }
                  setNotification({
                    message: res.message,
                    show: true,
                    title: 'Success',
                    valid: true,
                  });
                }}
              >
                Trigger Mailroom Sync
              </Button>
            )}
          </div>
        </div>

        {hasConnectedRegisteredAgentCompany && (
          <div className="flex items-center gap-2 mt-6">
            <span className="txt-sm text-text-secondary font-medium">
              <b>Registered Agents Inc Company Name:</b>{' '}
              {connectedRegisteredAgentCompany.name}
            </span>
          </div>
        )}
      </div>

      <hr className="my-8" />

      {/* Virtual Mailroom Section */}
      <div>
        <h1 className="txt-large-bold">Virtual Mailroom</h1>
        <p className="txt-sm text-text-secondary font-medium">
          This is the virtual mailroom for your company. It is used to send and
          receive mail to and from your company.
        </p>
        <div className="flex justify-end mt-4">
          {isStableSupportedState &&
            !hasVirtualMailroom &&
            !hasMailingAddress && (
              <Button
                onClick={addStableAddressModalVisibility.show}
                disabled={hasPendingJobs || hasMailingAddress}
                size="sm"
              >
                Add Location in Stable
              </Button>
            )}
        </div>
        {!isStableSupportedState && (
          <span className="txt-sm text-text-error font-medium">
            Virtual Mailroom is not supported in this jurisdiction:{' '}
            {labelOf(jurisdiction)}
          </span>
        )}
      </div>

      <hr className="my-8" />

      {/* Mail Viewing Section */}
      <div>
        <h1 className="txt-large-bold mb-4">Mail Documents</h1>

        {mailItemsWithMailroom.length === 0 ? (
          <EmptyStateV2
            title="No mail yet"
            description="No mail received at this address."
            icon={<Icons.Mail01 />}
          />
        ) : (
          <div className="flex flex-1 overflow-hidden border border-border-primary rounded-lg">
            {/* Left Panel */}
            <div className="w-full md:w-[400px] border-r flex flex-col">
              <div className="p-4 border-b">
                <h2 className="text-xl font-semibold">Inbox</h2>
              </div>

              <Tabs
                className="w-full"
                defaultValue={MailroomTab.ALL}
                value={mailTab}
                onValueChange={handleTabChange}
              >
                <HorizontalTabsList
                  className="w-full border-b gap-5"
                  variant="border"
                >
                  <TabsTrigger value="all">
                    All Mails ({mailItemsWithMailroom.length})
                  </TabsTrigger>
                  <TabsTrigger value="unread">
                    Unread ({unreadMails.length})
                  </TabsTrigger>
                </HorizontalTabsList>
                <TabsContent
                  value={MailroomTab.ALL}
                  className="overflow-y-auto h-[600px]"
                >
                  {mailItemsWithMailroom.map((mail) => (
                    <MailCard
                      key={mail.id}
                      mail={mail}
                      selectedMailId={selectedMailId}
                      setSelectedMailId={handleMailRead}
                    />
                  ))}
                </TabsContent>
                <TabsContent
                  value={MailroomTab.UNREAD}
                  className={cn(
                    'overflow-y-auto h-[600px] overflow-x-hidden',
                    unreadMails.length === 0 && 'h-[600px]'
                  )}
                >
                  {unreadMails.length === 0 && (
                    <EmptyStateV2
                      title="No unread mails"
                      description="All mails have been read"
                      icon={<Icons.Mail01 />}
                    />
                  )}
                  {unreadMails.map((mail) => (
                    <MailCard
                      key={mail.id}
                      mail={mail}
                      selectedMailId={selectedMailId}
                      setSelectedMailId={handleMailRead}
                    />
                  ))}
                </TabsContent>
              </Tabs>
            </div>

            {/* Right Panel - Mail Detail View */}
            {selectedMail && <MailItemPreview mail={selectedMail} />}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddStableAddressModal
        isOpen={addStableAddressModalVisibility.visible}
        onClose={addStableAddressModalVisibility.off}
        businessEntityId={businessEntityId}
        jurisdictionValue={jurisdiction}
        onSuccess={() => {
          mutateMailroomJobs();
          refetchBusinessEntity();
        }}
      />
      <AddExistingDRACompanyModal
        isOpen={addExistingDRACompanyModalVisibility.visible}
        onClose={() => {
          addExistingDRACompanyModalVisibility.off();
          refetchMailroomData();
          mutateRegisteredCompanies();
        }}
        corporationId={businessEntityId}
      />
    </div>
  );
};
